import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Plus, Eye, Pencil, Trash2, Lock, Unlock, Calendar, Users } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import { PageHeader, AppButton, AppBadge, SearchInput, EmptyState, ConfirmModal } from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import "./general-assignments.scss";

const statusVariant = {
  published: "primary",
  closed: "secondary",
};

const getRoutePrefix = () => {
  const user = getCurrentUser();
  return user?.usertype === "instructor" ? "/i/batch-assignments" : "/batch-assignments";
};

const GeneralAssignmentList = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteModal, setDeleteModal] = useState({ show: false, id: null, title: "" });
  const navigate = useNavigate();

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/general-assignments/list");
      setAssignments(data.assignments || []);
    } catch {
      setToast({ show: true, message: "Failed to load assignments", type: "error" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchAssignments(); }, []);

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/general-assignments/${deleteModal.id}`);
      setToast({ show: true, message: "Assignment deleted", type: "success" });
      setDeleteModal({ show: false, id: null, title: "" });
      fetchAssignments();
    } catch {
      setToast({ show: true, message: "Failed to delete", type: "error" });
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const endpoint = currentStatus === "published" ? "close" : "reopen";
      await apiClient.post(`/general-assignments/${id}/${endpoint}`);
      fetchAssignments();
    } catch {
      setToast({ show: true, message: "Failed to update status", type: "error" });
    }
  };

  const filtered = assignments.filter(
    (a) =>
      a.title?.toLowerCase().includes(search.toLowerCase()) ||
      a.batchId?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="ga-page">
      <PageHeader
        icon={ClipboardList}
        title="General Assignments"
        description="Create and manage text/file-based assignments for batches"
        actions={
          <AppButton onClick={() => navigate(`${getRoutePrefix()}/create`)}>
            <Plus size={16} /> Create Assignment
          </AppButton>
        }
      />

      <div className="ga-list__toolbar">
        <div className="ga-list__search">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assignments..."
          />
        </div>
      </div>

      {loading ? (
        <div className="ga-loading">Loading...</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<ClipboardList size={40} />}
          title={search ? "No matching assignments" : "No assignments yet"}
          description={search ? "Try a different search term" : "Create your first general assignment"}
          action={
            !search && (
              <AppButton onClick={() => navigate(`${getRoutePrefix()}/create`)}>
                <Plus size={16} /> Create Assignment
              </AppButton>
            )
          }
        />
      ) : (
        <div className="ga-list__table-wrap">
          <table className="ga-list__table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Batch</th>
                <th>Due Date</th>
                <th>Max Marks</th>
                <th>Submissions</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a._id}>
                  <td className="ga-list__title-cell">{a.title}</td>
                  <td>{a.batchId?.name || "—"}</td>
                  <td>
                    <span className="ga-detail__meta-item">
                      <Calendar size={14} /> {formatDate(a.dueDate)}
                    </span>
                  </td>
                  <td>{a.maxMarks}</td>
                  <td>
                    <span className="ga-detail__meta-item">
                      <Users size={14} /> {a.submissionCount || 0}
                      {a.gradedCount > 0 && ` (${a.gradedCount} graded)`}
                    </span>
                  </td>
                  <td>
                    <AppBadge variant="outline" color={statusVariant[a.status]}>
                      {a.status}
                    </AppBadge>
                  </td>
                  <td>
                    <div className="ga-list__actions">
                      <button
                        className="ga-list__action-btn"
                        title="View"
                        onClick={() => navigate(`${getRoutePrefix()}/${a._id}`)}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="ga-list__action-btn"
                        title="Edit"
                        onClick={() => navigate(`${getRoutePrefix()}/edit/${a._id}`)}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="ga-list__action-btn"
                        title={a.status === "published" ? "Close" : "Reopen"}
                        onClick={() => toggleStatus(a._id, a.status)}
                      >
                        {a.status === "published" ? <Lock size={16} /> : <Unlock size={16} />}
                      </button>
                      <button
                        className="ga-list__action-btn ga-list__action-btn--danger"
                        title="Delete"
                        onClick={() => setDeleteModal({ show: true, id: a._id, title: a.title })}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        show={deleteModal.show}
        title="Delete Assignment"
        message={`Are you sure you want to delete "${deleteModal.title}"? This will also delete all student submissions.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ show: false, id: null, title: "" })}
      />

      <CommonToaster toast={toast} setToast={setToast} />
    </div>
  );
};

export default GeneralAssignmentList;
